/**
 * Moving bytes: the engine that has to beat GitLab LFS.
 *
 * THE IDEA IN ONE PARAGRAPH
 *   Storage is content-addressed by SHA-256. So the client hashes a file
 *   FIRST, asks the server "do you already hold this content?", and when the
 *   answer is yes the upload is over before a progress bar could have drawn a
 *   frame — zero bytes move. Dropping a folder of 200 SolidWorks parts onto
 *   Lattice after changing three of them is three uploads, not two hundred.
 *   When bytes do have to move they go straight from the browser to object
 *   storage; the API only ever brokers a short-lived URL. Nothing queues
 *   behind a middleman, which is precisely what made LFS slow for this team.
 *
 * WHAT THIS MODULE IS NOT
 *   It holds no state of its own and knows nothing about React. Everything it
 *   learns it reports through `onTransfer`; the store in store/transfers.ts
 *   turns that stream of records into something the interface can draw. That
 *   split is what lets the whole pipeline be reasoned about — and tested —
 *   without a renderer.
 *
 * DATA DEFINITIONS
 *
 *   A TransferId is a String, unique within this browsing session.
 *   Interpretation: the identity of one file's journey. Every record emitted
 *   about that file carries it, so the store can update in place.
 *
 *   A TransferDirection is one of "upload" | "download".
 *
 *   A TransferPhase is one of:
 *     "queued"      accepted; waiting for a slot
 *     "hashing"     reading the bytes to learn their SHA-256
 *     "checking"    asking storage whether it already holds that content
 *     "uploading"   bytes are moving to object storage
 *     "committing"  storage has the bytes; recording the change in the log
 *     "downloading" bytes are moving from storage to this machine
 *     "done"        finished, and bytes really moved
 *     "skipped"     finished, and NO bytes moved — storage already had this
 *                   exact content. The happy path, and usually the common one.
 *     "failed"      gave up; `error` says why
 *     "cancelled"   the person stopped it
 *   Interpretation: where one file is in its journey. The last four are
 *   terminal: a transfer that reaches one never changes again.
 *
 *   A Transfer is the structure below.
 *   Interpretation: everything known about one file's journey at one instant.
 *   Records are immutable — a change produces a new record with the same id —
 *   so a store can compare by identity and a list can render by reference.
 *
 *   `bytesDone` is progress WITHIN the current phase, out of `totalBytes`:
 *   bytes hashed while hashing, bytes sent while uploading. It resets when the
 *   phase changes. A phase that moves no bytes (checking, committing) leaves
 *   it at zero, which is how the interface knows not to draw a bar.
 */
import { api, ApiError, type DownloadItem, type UploadTicket } from '../store/api'
import { abortError, hashBlob } from '../workers/hash.worker'
import type { HashRequest, HashResponse } from '../workers/hash.worker'
import type { Change } from '@lattice/core'
import { downloadZip, predictLength } from 'client-zip'

export type TransferId = string
export type TransferDirection = 'upload' | 'download'

export type TransferPhase =
  | 'queued' | 'hashing' | 'checking' | 'uploading' | 'committing' | 'downloading'
  | 'done' | 'skipped' | 'failed' | 'cancelled'

export interface Transfer {
  readonly id: TransferId
  readonly direction: TransferDirection
  /** What to show a person: the file's own name, or the zip's. */
  readonly name: string
  /** Project-relative path, or "" for an aggregate (a multi-file zip). */
  readonly path: string
  readonly projectId: string
  readonly phase: TransferPhase
  readonly bytesDone: number
  readonly totalBytes: number
  /** Bytes that actually crossed the network. Zero for a skip — the whole point. */
  readonly bytesMoved: number
  readonly startedAt: number
  readonly endedAt?: number
  readonly sha256?: string
  readonly error?: string
}

const TERMINAL: ReadonlySet<TransferPhase> = new Set<TransferPhase>(['done', 'skipped', 'failed', 'cancelled'])

/**
 * Has this transfer stopped for good?
 * isTerminal : TransferPhase -> Boolean
 */
export const isTerminal = (phase: TransferPhase): boolean => TERMINAL.has(phase)

/** Did this transfer finish without moving any bytes? Used to keep the UI quiet. */
export const wasInstant = (t: Transfer): boolean => t.phase === 'skipped'

/** How many files move at once. Four keeps a laptop's uplink busy without starving it. */
const DEFAULT_CONCURRENCY = 4

/** Progress is pushed at most this often per file; more would only cost renders. */
const PROGRESS_INTERVAL_MS = 120

let counter = 0
/** A fresh transfer id. nextTransferId : -> TransferId */
const nextTransferId = (): TransferId => `t${Date.now().toString(36)}${(counter++).toString(36)}`

/* ------------------------------------------------------------------ */
/* Reporting                                                           */
/* ------------------------------------------------------------------ */

/** A caller's window onto one file: every new record, plus a way to stop it. */
export type TransferListener = (transfer: Transfer, cancel: () => void) => void

/**
 * The application's standing interest in transfers, wherever they were started.
 *
 * WHY THIS EXISTS
 *   A download can be started from the command palette, a context menu, a
 *   toolbar or a keyboard shortcut. Every one of those must show up in the
 *   transfer tray, and none of them should have to know the tray exists. So
 *   the engine reports to one registered observer — the same shape as the sync
 *   engine's `onChange` — and callers that want a private listener as well may
 *   still pass one.
 *
 * DATA DEFINITION
 *   A TransferObserver is a record of three optional callbacks:
 *     onTransfer  every state a transfer passes through
 *     onChanges   the log rows a commit produced, so the tree updates at once
 *     hasContent  "does storage already hold this hash?", answered from the
 *                 local replica (see UploadOptions.hasContent)
 *   Interpretation: what the application as a whole wants to know and can
 *   answer. There is exactly one, because there is exactly one workspace.
 */
export interface TransferObserver {
  readonly onTransfer?: TransferListener
  readonly onChanges?: (changes: readonly Change[]) => void
  readonly hasContent?: (sha256: string) => boolean
}

let observer: TransferObserver = {}

/**
 * Register the application's observer; returns the undo.
 * observeTransfers : TransferObserver -> (-> void)
 */
export function observeTransfers(next: TransferObserver): () => void {
  observer = next
  return () => { if (observer === next) observer = {} }
}

/** Report to the caller's listener and to the application's, in that order. */
const fanout = (own: TransferListener | undefined): TransferListener => (transfer, cancel) => {
  own?.(transfer, cancel)
  if (observer.onTransfer !== own) observer.onTransfer?.(transfer, cancel)
}

/**
 * A live record plus the two ways it changes.
 *
 * Interpretation: `to` marks a phase boundary (always reported, they are rare
 * and meaningful); `at` reports byte progress (throttled, there are thousands).
 */
interface Reporter {
  readonly current: () => Transfer
  readonly to: (phase: TransferPhase, patch?: Partial<Transfer>) => Transfer
  readonly at: (bytesDone: number) => void
  readonly patch: (patch: Partial<Transfer>) => Transfer
}

/** Build a reporter around an initial record. makeReporter : Transfer ... -> Reporter */
function makeReporter(initial: Transfer, listener: TransferListener | undefined, cancel: () => void): Reporter {
  let record = initial
  let lastPush = 0

  const push = (): void => listener?.(record, cancel)
  const patch = (changes: Partial<Transfer>): Transfer => {
    record = { ...record, ...changes }
    push()
    return record
  }

  push()
  return {
    current: () => record,
    patch,
    to: (phase, changes) => patch({
      phase,
      // A new phase measures a new thing, so the bar starts again — except at
      // the end, where leaving the count alone avoids a flicker back to zero.
      bytesDone: isTerminal(phase) ? record.bytesDone : 0,
      endedAt: isTerminal(phase) ? Date.now() : undefined,
      ...changes,
    }),
    at: bytesDone => {
      // Progress within a phase only ever goes forward. A retried PUT starts
      // counting from zero again, and without this the file's bar — and the
      // aggregate bar the header draws from it — would snap back to 0% at 80%
      // because a hotel wifi hiccup dropped one connection. Holding the old
      // figure until the retry catches up is the honest reading: those bytes
      // are still the furthest we have got.
      if (bytesDone < record.bytesDone) return
      const now = Date.now()
      if (now - lastPush < PROGRESS_INTERVAL_MS && bytesDone < record.totalBytes) return
      lastPush = now
      patch({ bytesDone })
    },
  }
}

/* ------------------------------------------------------------------ */
/* Uploading                                                           */
/* ------------------------------------------------------------------ */

/** One file the person wants stored, and where it should live in the project. */
export interface UploadFile {
  readonly file: File
  /** Project-relative path, e.g. "Cathode/30kV_Cup.SLDPRT". */
  readonly path: string
}

export interface UploadOptions {
  /** The note attached to every change this batch writes. */
  readonly message?: string
  readonly concurrency?: number
  readonly signal?: AbortSignal
  readonly onTransfer?: TransferListener
  /**
   * "Do we already know storage holds this content?"
   *
   * The caller can answer from the local replica: a blob referenced by any
   * change in the log is, by construction, stored — commits are refused until
   * the bytes are confirmed, and content-addressed blobs are never deleted. A
   * `true` here saves a round trip per file, which on a 200-file drop is the
   * difference between a moment and a minute. Being wrong is safe: the commit
   * is refused with 409 and we fall back to the full path.
   */
  readonly hasContent?: (sha256: string) => boolean
}

export interface UploadSummary {
  readonly transfers: readonly Transfer[]
  /** The changes the server wrote, so the caller can show them without waiting for sync. */
  readonly changes: readonly Change[]
  readonly uploaded: number
  readonly skipped: number
  readonly failed: number
  readonly cancelled: number
  readonly bytesMoved: number
  readonly elapsedMs: number
}

/** Everything one batch of uploads shares. */
interface Batch {
  /**
   * sha256 -> the in-flight attempt to store it, so identical files upload once.
   *
   * The promise resolves TRUE when storage ended up holding the content and
   * FALSE when that attempt gave up; it never rejects. Followers must not
   * inherit the leader's fate — see `ensureStored` — and a promise nobody is
   * waiting on yet must not be able to raise an unhandled rejection.
   */
  readonly inflight: Map<string, Promise<boolean>>
  readonly changes: Change[]
}

/**
 * Store files and record them in the project's log.
 *
 * Per file: hash -> ask -> (skip | send) -> commit. Bounded concurrency, with
 * every step cancellable. Never rejects for an individual file's sake: a
 * failure is reported on that file's record and the rest of the batch carries
 * on, because losing 199 good uploads to one bad one is not acceptable.
 * uploadFiles : String [UploadFile] UploadOptions -> Promise<UploadSummary>
 */
export async function uploadFiles(
  projectId: string,
  files: readonly UploadFile[],
  options: UploadOptions = {},
): Promise<UploadSummary> {
  const startedAt = Date.now()
  const batch: Batch = { inflight: new Map(), changes: [] }
  const finished: Transfer[] = []

  // Announce the whole drop before starting any of it. Someone who drags 200
  // parts in should see 200 rows at once — not four, then four more — and the
  // aggregate bar needs the total to be known from the first frame.
  const jobs = files.map(entry => prepare(projectId, entry, options))

  await forEachLimit(jobs, options.concurrency ?? DEFAULT_CONCURRENCY, async job => {
    finished.push(await uploadOne(job, batch, options))
  })

  const total = (phase: TransferPhase): number => finished.filter(t => t.phase === phase).length
  return {
    transfers: finished,
    changes: batch.changes,
    uploaded: total('done'),
    skipped: total('skipped'),
    failed: total('failed'),
    cancelled: total('cancelled'),
    bytesMoved: finished.reduce((sum, t) => sum + t.bytesMoved, 0),
    elapsedMs: Date.now() - startedAt,
  }
}

/**
 * One file's paperwork, done up front.
 *
 * DATA DEFINITION
 *   An UploadJob is { entry, report, control, unlink }.
 *   Interpretation: a file that has been accepted and is visible to the person
 *   — queued, cancellable, counted in the totals — but whose bytes have not
 *   been touched yet.
 */
interface UploadJob {
  readonly projectId: string
  readonly entry: UploadFile
  readonly report: Reporter
  readonly control: AbortController
  readonly unlink: () => void
}

/** Accept a file: give it an id, a row and a way to be stopped. prepare : ... -> UploadJob */
function prepare(projectId: string, entry: UploadFile, options: UploadOptions): UploadJob {
  const control = new AbortController()
  const unlink = linkAbort(options.signal, control)
  const report = makeReporter({
    id: nextTransferId(),
    direction: 'upload',
    name: leafOf(entry.path) || entry.file.name,
    path: entry.path,
    projectId,
    phase: 'queued',
    bytesDone: 0,
    totalBytes: entry.file.size,
    bytesMoved: 0,
    startedAt: Date.now(),
  }, fanout(options.onTransfer), () => control.abort())
  return { projectId, entry, report, control, unlink }
}

/**
 * One file, end to end. Resolves with its final record; never rejects.
 * uploadOne : UploadJob Batch UploadOptions -> Promise<Transfer>
 */
async function uploadOne(job: UploadJob, batch: Batch, options: UploadOptions): Promise<Transfer> {
  const { projectId, entry: { file, path }, report, control, unlink } = job

  try {
    throwIfAborted(control.signal)

    report.to('hashing')
    const sha256 = await hashFile(file, n => report.at(n), control.signal)
    report.patch({ sha256 })

    report.to('checking')
    const contentType = contentTypeOf(file, path)
    // The instant path: believe the local replica and skip the round trip.
    const knows = options.hasContent ?? observer.hasContent
    let assumed = knows?.(sha256) === true
    let moved = assumed ? 0 : await ensureStored(batch, sha256, file, contentType, report, control.signal)

    report.to('committing')
    let written: readonly Change[]
    try {
      written = (await api.commitFile({ projectId, path, sha256, size: file.size, message: options.message })).changes
    } catch (error) {
      // We were wrong about storage holding the content. Send it for real.
      if (!(assumed && error instanceof ApiError && error.status === 409)) throw error
      assumed = false
      moved = await ensureStored(batch, sha256, file, contentType, report, control.signal)
      report.to('committing')
      written = (await api.commitFile({ projectId, path, sha256, size: file.size, message: options.message })).changes
    }
    batch.changes.push(...written)
    // Tell the application at once: the row in the tree should be there before
    // the upload's own row turns green, not a sync round trip later.
    if (written.length > 0) observer.onChanges?.(written)

    // Nothing to write means the project already had exactly these bytes at
    // exactly this path. Present that as the same instant success as a skip.
    return report.to(moved > 0 ? 'done' : 'skipped', { bytesMoved: moved, bytesDone: moved })
  } catch (error) {
    if (isAbort(error)) return report.to('cancelled')
    return report.to('failed', { error: messageOf(error) })
  } finally {
    unlink()
  }
}

/**
 * Make sure object storage holds `sha256`, sending the bytes only if it does not.
 * Returns how many bytes actually crossed the network.
 *
 * Two files in one batch with identical content — common, engineers copy
 * parts — share a single attempt rather than racing each other.
 *
 * WHY THE FOLLOWER DOES NOT SIMPLY AWAIT THE LEADER'S PROMISE
 *   The leader carries ITS OWN abort signal. Dropping `cup-A.STEP` and
 *   `cup-B.STEP` (identical parts, different paths) and then pressing stop on A
 *   used to cancel B as well, silently, without B ever being uploaded — B had
 *   inherited A's AbortError. The same held for a failure: one dropped
 *   connection took every copy down with it. So the shared promise reports only
 *   WHETHER the content got stored, and a follower whose leader gave up takes
 *   over and does the work itself.
 * ensureStored : ... -> Promise<Number>
 */
async function ensureStored(
  batch: Batch, sha256: string, file: File, contentType: string,
  report: Reporter, signal: AbortSignal,
): Promise<number> {
  // Loops rather than recurses, but terminates for the same reason: every
  // round either stores the content or removes a leader, and there are only as
  // many possible leaders as there are files sharing this hash.
  for (;;) {
    const pending = batch.inflight.get(sha256)
    if (!pending) break
    if (await pending) return 0
    // That leader gave up. This file still has to be stored — unless the
    // person has since stopped THIS one too.
    throwIfAborted(signal)
  }

  const settled = deferred<boolean>()
  batch.inflight.set(sha256, settled.promise)
  try {
    const sent = await storeContent(sha256, file, contentType, report, signal)
    settled.resolve(true)
    return sent
  } catch (error) {
    // Clear the slot before waking the followers, so the first one through
    // finds no leader and becomes one.
    batch.inflight.delete(sha256)
    settled.resolve(false)
    throw error
  }
}

/** A promise plus the handle to settle it later. deferred : -> {promise, resolve} */
function deferred<T>(): { promise: Promise<T>; resolve: (value: T) => void } {
  let resolve!: (value: T) => void
  const promise = new Promise<T>(r => { resolve = r })
  return { promise, resolve }
}

/** Negotiate, then send if needed. storeContent : ... -> Promise<Number> (bytes sent) */
async function storeContent(
  sha256: string, file: File, contentType: string, report: Reporter, signal: AbortSignal,
): Promise<number> {
  const ticket: UploadTicket = await api.negotiateUpload(sha256, file.size, contentType)
  if (ticket.kind === 'exists') return 0

  report.to('uploading')
  await putBlob(ticket, file, n => report.at(n), signal)
  await api.confirmUpload(sha256)
  return file.size
}

/** How many times to re-send after a network failure before giving up. */
const UPLOAD_ATTEMPTS = 3

/**
 * PUT the bytes straight to object storage, reporting progress.
 *
 * XMLHttpRequest, not fetch: fetch still cannot report upload progress in any
 * shipping browser (a streaming request body needs HTTP/2 and `duplex`, and is
 * not universally available), and a 400 MB assembly with no progress bar feels
 * broken. This is the one place XHR earns its keep.
 * putBlob : UploadTicket Blob (Number -> void) AbortSignal -> Promise<void>
 */
function putBlob(
  ticket: Extract<UploadTicket, { kind: 'upload' }>,
  body: Blob,
  onProgress: (bytesSent: number) => void,
  signal: AbortSignal,
): Promise<void> {
  return withRetries(UPLOAD_ATTEMPTS, signal, () => new Promise<void>((resolve, reject) => {
    // An `abort` event does not replay for a listener added afterwards. Without
    // this check, a cancel that landed while `negotiateUpload` was in flight
    // would be lost completely: the whole 400 MB would go up, get confirmed and
    // get committed, and the row would turn green. Verified, not theorised.
    if (signal.aborted) { reject(abortError()); return }

    const request = new XMLHttpRequest()
    const onAbort = (): void => request.abort()

    request.open(ticket.method, ticket.url, true)
    for (const [name, value] of Object.entries(ticket.headers)) request.setRequestHeader(name, value)

    request.upload.onprogress = event => { if (event.lengthComputable) onProgress(event.loaded) }
    request.onload = () => {
      signal.removeEventListener('abort', onAbort)
      if (request.status >= 200 && request.status < 300) { onProgress(body.size); resolve(); return }
      reject(new Error(`storage refused the upload (HTTP ${request.status})`))
    }
    request.onerror = () => { signal.removeEventListener('abort', onAbort); reject(new Error('the network dropped mid-upload')) }
    request.onabort = () => { signal.removeEventListener('abort', onAbort); reject(abortError()) }

    signal.addEventListener('abort', onAbort, { once: true })
    request.send(body)
  }))
}

/* ------------------------------------------------------------------ */
/* Hashing, pooled across a few workers                                */
/* ------------------------------------------------------------------ */

/**
 * Hash a file off the main thread, on the first free worker.
 *
 * Falls back to hashing here, yielding between chunks, wherever workers are
 * unavailable. Slower, but the application still works, which is the deal.
 * hashFile : File (Number -> void) AbortSignal -> Promise<String>
 */
async function hashFile(file: File, onProgress: (bytes: number) => void, signal: AbortSignal): Promise<string> {
  const worker = await takeWorker()
  if (!worker) return hashBlob(file, { onProgress, signal, yieldToHost: true })
  try {
    return await hashOnWorker(worker, file, onProgress, signal)
  } finally {
    releaseWorker(worker)
  }
}

interface Pooled { readonly worker: Worker; busy: boolean }

/**
 * A small pool of hashing workers.
 *
 * Interpretation: hashing is CPU-bound, so the pool is sized by cores, not by
 * how many files are in flight — more workers than cores would only make every
 * file finish later. They are kept warm for the session: starting one costs
 * more than the hash of a typical drawing.
 */
const POOL_SIZE = Math.max(1, Math.min(3, (globalThis.navigator?.hardwareConcurrency ?? 4) - 1))
const pool: Pooled[] = []
const waiting: ((worker: Pooled | undefined) => void)[] = []
let workersUsable = true

/**
 * How to give up on every hash currently running on a worker.
 *
 * Interpretation: one entry per in-flight `hashOnWorker`, removed as soon as
 * that hash settles by itself. It exists only so `disposeHashWorkers` can end
 * them — see the note there.
 */
const abandonHash = new Set<(reason: Error) => void>()

/** Claim a worker, waiting for one if all are busy. Resolves undefined if unsupported. */
function takeWorker(): Promise<Pooled | undefined> {
  if (!workersUsable || typeof Worker === 'undefined') return Promise.resolve(undefined)

  const idle = pool.find(p => !p.busy)
  if (idle) { idle.busy = true; return Promise.resolve(idle) }

  if (pool.length < POOL_SIZE) {
    try {
      const entry: Pooled = {
        worker: new Worker(new URL('../workers/hash.worker.ts', import.meta.url), { type: 'module' }),
        busy: true,
      }
      pool.push(entry)
      return Promise.resolve(entry)
    } catch {
      // A browser (or a test runner) without module workers. Say so once.
      workersUsable = false
      return Promise.resolve(undefined)
    }
  }
  return new Promise(resolve => waiting.push(resolve))
}

/** Hand a worker back, giving it straight to whoever is next in line. */
function releaseWorker(entry: Pooled): void {
  const next = waiting.shift()
  if (next) { next(entry); return }
  entry.busy = false
}

/**
 * Shut the pool down. Used on sign-out, and by tests.
 *
 * `terminate()` fires neither a message nor an error event, so a hash still
 * running on a worker would leave its promise pending for ever: the upload
 * that started it would never settle, its row would sit at "hashing" until the
 * tab closed, and the File it holds would never be collected. So the running
 * hashes are given up on FIRST, and only then is the plug pulled.
 * disposeHashWorkers : -> void
 */
export function disposeHashWorkers(): void {
  for (const abandon of [...abandonHash]) abandon(abortError())
  for (const entry of pool) entry.worker.terminate()
  pool.length = 0
  while (waiting.length > 0) waiting.shift()?.(undefined)
}

/** Run one hash on a claimed worker. hashOnWorker : ... -> Promise<String> */
function hashOnWorker(
  entry: Pooled, file: File, onProgress: (bytes: number) => void, signal: AbortSignal,
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    // `abort` never replays for a listener added after the fact, so a signal
    // that is already aborted would otherwise hash the whole file and hand
    // back a digest nobody wants.
    if (signal.aborted) { reject(abortError()); return }

    const id = nextTransferId()
    const post = (message: HashRequest): void => entry.worker.postMessage(message)

    const cleanup = (): void => {
      entry.worker.removeEventListener('message', onMessage)
      entry.worker.removeEventListener('error', onError)
      signal.removeEventListener('abort', onAbort)
      abandonHash.delete(abandon)
    }
    /** Give up without hearing back — the worker is about to be terminated. */
    const abandon = (reason: Error): void => { cleanup(); reject(reason) }
    const onMessage = (event: MessageEvent<HashResponse>): void => {
      const response = event.data
      if (response.id !== id) return
      if (response.type === 'progress') { onProgress(response.bytesHashed); return }
      cleanup()
      if (response.type === 'done') resolve(response.sha256)
      else if (response.type === 'cancelled') reject(abortError())
      else reject(new Error(response.message))
    }
    const onError = (): void => { cleanup(); reject(new Error('the hashing worker stopped')) }
    const onAbort = (): void => post({ type: 'cancel', id })

    abandonHash.add(abandon)
    entry.worker.addEventListener('message', onMessage)
    entry.worker.addEventListener('error', onError)
    signal.addEventListener('abort', onAbort, { once: true })
    // A File crosses to a worker by reference: the bytes are not copied.
    post({ type: 'hash', id, blob: file })
  })
}

/* ------------------------------------------------------------------ */
/* Downloading                                                         */
/* ------------------------------------------------------------------ */

export interface DownloadOptions {
  readonly signal?: AbortSignal
  readonly onTransfer?: TransferListener
  /** Overrides the name chosen for a multi-file zip. */
  readonly zipName?: string
  /** Modification time to stamp on a zip entry, from the file's history. */
  readonly lastModifiedOf?: (path: string) => Date | undefined
  /** Used when the selection has no folder in common. Usually the project name. */
  readonly label?: string
  /** How many files to fetch ahead of the zip writer. Two is latency hidden without hoarding memory. */
  readonly lookahead?: number
}

export interface DownloadSummary {
  /** "single" the browser saved one file; "zip" we built an archive; "none" nothing was available. */
  readonly kind: 'single' | 'zip' | 'none'
  readonly files: number
  readonly bytes: number
  /** Paths the server could not serve, with the reason it gave. */
  readonly unavailable: readonly { path: string; reason: string }[]
  readonly cancelled: boolean
}

/**
 * Save files to the person's machine.
 *
 * One file goes straight to the browser's own downloader — its bytes never
 * enter this tab, so a 2 GB assembly costs us no memory and gets the browser's
 * own resume and progress. Several files are zipped HERE, streaming, so the
 * server is not asked to build an archive (that would double the bytes leaving
 * Google and delay the first one until the last file was read).
 * downloadFiles : String [String] DownloadOptions -> Promise<DownloadSummary>
 */
export async function downloadFiles(
  projectId: string,
  paths: readonly string[],
  options: DownloadOptions = {},
): Promise<DownloadSummary> {
  if (paths.length === 0) return { kind: 'none', files: 0, bytes: 0, unavailable: [], cancelled: false }

  // Ask for the save location FIRST, while the click that started this is
  // still fresh: the picker is gated on user activation, and a round trip to
  // the API can outlive it.
  const zipName = options.zipName ?? zipNameFor(paths, options.label ?? projectId)
  const destination = paths.length > 1 ? await chooseDestination(zipName) : undefined
  if (destination === 'cancelled') return { kind: 'none', files: 0, bytes: 0, unavailable: [], cancelled: true }

  // Asking early means we can also be left holding a save location we turn out
  // not to need — the request may fail, or the server may only be able to serve
  // one of the files. Every path out of here has to hand it back.
  const listing = await api.downloadUrls(projectId, [...paths]).catch(async (error: unknown) => {
    await discardDestination(destination)
    throw error
  })
  const items = listing.items
  const ready = items.filter(hasUrl)
  const unavailable = items.filter(item => !item.url)
    .map(item => ({ path: item.path, reason: item.reason ?? 'unavailable' }))

  if (ready.length < 2) await discardDestination(destination)
  if (ready.length === 0) return { kind: 'none', files: 0, bytes: 0, unavailable, cancelled: false }
  if (ready.length === 1) return saveOne(projectId, ready[0], unavailable, options)
  return saveZip(projectId, ready, zipName, destination, unavailable, options)
}

/**
 * Hand back a save location we asked for and are not going to write to.
 *
 * Chromium CREATES the file the moment the picker resolves, so walking away
 * leaves a zero-byte .zip sitting in the person's Downloads folder with a
 * writable still open on it. Select two files where the server can only serve
 * one and that is exactly what used to happen.
 * discardDestination : FileSystemWritableFileStream|undefined -> Promise<void>
 */
async function discardDestination(destination: FileSystemWritableFileStream | undefined): Promise<void> {
  await destination?.abort().catch(() => undefined)
}

/** A DownloadItem the server was able to sign a URL for. */
type ReadyItem = DownloadItem & { readonly url: string }
const hasUrl = (item: DownloadItem): item is ReadyItem => typeof item.url === 'string' && item.url.length > 0

/**
 * Hand one file to the browser's downloader.
 *
 * The URL carries a content-disposition naming the file, so this saves under
 * the right name even though `download` is ignored cross-origin.
 * saveOne : ... -> DownloadSummary
 */
function saveOne(
  projectId: string, item: ReadyItem,
  unavailable: readonly { path: string; reason: string }[],
  options: DownloadOptions,
): DownloadSummary {
  const report = makeReporter({
    id: nextTransferId(),
    direction: 'download',
    name: item.name,
    path: item.path,
    projectId,
    phase: 'downloading',
    bytesDone: 0,
    totalBytes: item.size,
    bytesMoved: 0,
    startedAt: Date.now(),
  }, fanout(options.onTransfer), () => undefined)

  clickToDownload(item.url, item.name)
  // The browser owns the transfer from here and will not tell us how it goes,
  // so the honest thing to report is the handoff, not a fake progress bar.
  report.to('done', { bytesDone: item.size, bytesMoved: item.size })
  return { kind: 'single', files: 1, bytes: item.size, unavailable, cancelled: false }
}

/**
 * Build a ZIP in this tab, streaming, and write it to disk.
 *
 * STORE ONLY, never deflate: STEP, SLDPRT and PDF are already compressed, so
 * deflating them burns CPU to save nothing. client-zip stores by design, which
 * is why it is here. Because nothing is compressed we can predict the archive's
 * exact size up front — so the progress bar is real, and the browser gets a
 * Content-Length instead of a spinner.
 * saveZip : ... -> Promise<DownloadSummary>
 */
async function saveZip(
  projectId: string, items: readonly ReadyItem[], zipName: string,
  destination: FileSystemWritableFileStream | undefined,
  unavailable: readonly { path: string; reason: string }[],
  options: DownloadOptions,
): Promise<DownloadSummary> {
  const control = new AbortController()
  const unlink = linkAbort(options.signal, control)

  const prefix = commonFolderPrefix(items.map(item => item.path))
  const entries = items.map(item => ({
    name: entryNameFor(item.path, prefix),
    size: item.size,
    lastModified: options.lastModifiedOf?.(item.path) ?? new Date(),
  }))
  const totalBytes = Number(predictLength(entries))

  const report = makeReporter({
    id: nextTransferId(),
    direction: 'download',
    name: zipName,
    path: '',
    projectId,
    phase: 'downloading',
    bytesDone: 0,
    totalBytes,
    bytesMoved: 0,
    startedAt: Date.now(),
  }, fanout(options.onTransfer), () => control.abort())

  try {
    const archive = downloadZip(
      zipSources(items, entries, options.lookahead ?? 2, control.signal),
      { length: totalBytes },
    )
    const counted = countingStream(archive.body, bytes => report.at(bytes))

    if (destination) await counted.pipeTo(destination, { signal: control.signal })
    else clickToDownloadBlob(await new Response(counted).blob(), zipName)

    report.to('done', { bytesDone: totalBytes, bytesMoved: totalBytes })
    return { kind: 'zip', files: items.length, bytes: totalBytes, unavailable, cancelled: false }
  } catch (error) {
    await discardDestination(destination)
    if (isAbort(error)) {
      report.to('cancelled')
      return { kind: 'none', files: 0, bytes: 0, unavailable, cancelled: true }
    }
    report.to('failed', { error: messageOf(error) })
    throw error
  } finally {
    unlink()
  }
}

/**
 * Yield each file's response in order, keeping a few fetches in the air.
 *
 * The zip writer pulls one entry at a time, so without look-ahead every file
 * would pay a fresh round trip's latency. With it, the next request is already
 * on the wire. The look-ahead is small on purpose: an unread response body is
 * buffered by the browser, and CAD files are not small.
 */
async function* zipSources(
  items: readonly ReadyItem[],
  entries: readonly { name: string; lastModified: Date }[],
  lookahead: number,
  signal: AbortSignal,
): AsyncGenerator<{ name: string; lastModified: Date; size: number; input: Response }> {
  const pending: Promise<Response>[] = []
  let next = 0
  const prime = (): void => {
    while (pending.length <= Math.max(0, lookahead) && next < items.length) {
      pending.push(fetch(items[next++].url, { signal, credentials: 'omit' }))
    }
  }

  try {
    for (let index = 0; index < items.length; index++) {
      prime()
      const response = await pending.shift()!
      if (!response.ok) {
        void response.body?.cancel().catch(() => undefined)
        throw new Error(`${items[index].name}: storage answered ${response.status}`)
      }
      yield { ...entries[index], size: items[index].size, input: response }
    }
  } finally {
    // The consumer can walk away at any entry — the person cancelled, the disk
    // filled, a later file 403'd. Whatever is still in the air has a body the
    // browser is buffering (CAD files are not small) and a promise that will
    // reject unheard the moment the signal aborts. Let all of it go.
    for (const inflight of pending) {
      void inflight.then(
        response => response.body?.cancel().catch(() => undefined),
        () => undefined,
      )
    }
    pending.length = 0
  }
}

/**
 * Pass a stream through unchanged, counting bytes as they go.
 * countingStream : ReadableStream (Number -> void) -> ReadableStream
 */
function countingStream(
  source: ReadableStream<Uint8Array> | null, onBytes: (total: number) => void,
): ReadableStream<Uint8Array> {
  if (!source) throw new Error('the archive produced no data')
  let total = 0
  return source.pipeThrough(new TransformStream<Uint8Array, Uint8Array>({
    transform(chunk, controller) {
      total += chunk.byteLength
      onBytes(total)
      controller.enqueue(chunk)
    },
  }))
}

/**
 * Ask where to put the archive, streaming straight to that file when the
 * browser allows it. Returns undefined when it does not (Safari, Firefox),
 * and "cancelled" when the person closed the dialog.
 * chooseDestination : String -> Promise<FileSystemWritableFileStream | undefined | "cancelled">
 */
async function chooseDestination(
  suggestedName: string,
): Promise<FileSystemWritableFileStream | undefined | 'cancelled'> {
  const picker = (globalThis as { showSaveFilePicker?: ShowSaveFilePicker }).showSaveFilePicker
  if (typeof picker !== 'function') return undefined
  try {
    const handle = await picker({
      suggestedName,
      types: [{ description: 'ZIP archive', accept: { 'application/zip': ['.zip'] } }],
    })
    return await handle.createWritable()
  } catch (error) {
    if (isAbort(error)) return 'cancelled'
    // Some environments (an iframe without permission) throw something else.
    // A blob download still works, so fall back rather than fail.
    return undefined
  }
}

/** The File System Access entry point, typed only as far as we use it. */
type ShowSaveFilePicker = (options: {
  suggestedName?: string
  types?: { description: string; accept: Record<string, string[]> }[]
}) => Promise<{ createWritable: () => Promise<FileSystemWritableFileStream> }>

/** The writable end of a file on disk. */
interface FileSystemWritableFileStream extends WritableStream<Uint8Array> {
  abort: () => Promise<void>
}

/* ------------------------------------------------------------------ */
/* Naming                                                              */
/* ------------------------------------------------------------------ */

/**
 * The longest folder path every one of these paths lies inside ("" when none).
 * commonFolderPrefix : [String] -> String
 */
export function commonFolderPrefix(paths: readonly string[]): string {
  if (paths.length === 0) return ''
  const folders = paths.map(path => path.split('/').slice(0, -1))
  const first = folders[0]
  let shared = first.length
  for (const other of folders.slice(1)) {
    shared = Math.min(shared, other.length)
    for (let i = 0; i < shared; i++) {
      if (other[i] !== first[i]) { shared = i; break }
    }
  }
  return first.slice(0, shared).join('/')
}

/**
 * Where a file sits inside the archive.
 *
 * Dropping the shared folder means "download this folder" produces a zip of
 * that folder's contents, not six levels of empty directories to click through.
 * entryNameFor : String String -> String
 */
export const entryNameFor = (path: string, prefix: string): string =>
  prefix && path.startsWith(`${prefix}/`) ? path.slice(prefix.length + 1) : path

/**
 * What to call the archive: the folder they picked, or a labelled count.
 * zipNameFor : [String] String -> String
 */
export function zipNameFor(paths: readonly string[], label: string): string {
  const folder = commonFolderPrefix(paths).split('/').filter(Boolean).at(-1)
  const stem = folder ?? `${label}-${paths.length}-files`
  return `${safeFilename(stem)}.zip`
}

/** Strip what an operating system will not accept in a filename. */
const safeFilename = (name: string): string =>
  name.replace(/[/\\:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim() || 'lattice'

/**
 * The media type to store a file under.
 *
 * WHY WE DO NOT TRUST `file.type`
 *   Browsers report "" for .SLDPRT, .STEP and most of what this team works
 *   with. The type is what decides whether a PDF previews inline later, so we
 *   name the formats we care about ourselves and fall back to what the browser
 *   thinks only when we have nothing better.
 * contentTypeOf : File String -> String
 */
export function contentTypeOf(file: Pick<File, 'type'>, path: string): string {
  const extension = path.slice(path.lastIndexOf('.') + 1).toLowerCase()
  return CONTENT_TYPES[extension] ?? (file.type || 'application/octet-stream')
}

const CONTENT_TYPES: Readonly<Record<string, string>> = {
  pdf: 'application/pdf',
  step: 'model/step', stp: 'model/step',
  iges: 'model/iges', igs: 'model/iges',
  stl: 'model/stl',
  dxf: 'image/vnd.dxf', dwg: 'image/vnd.dwg',
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
  gif: 'image/gif', webp: 'image/webp', svg: 'image/svg+xml', bmp: 'image/bmp', tif: 'image/tiff', tiff: 'image/tiff',
  txt: 'text/plain', md: 'text/markdown', csv: 'text/csv', json: 'application/json', xml: 'application/xml',
  zip: 'application/zip', '7z': 'application/x-7z-compressed',
  // Named deliberately: SolidWorks files are opaque, and saying so beats
  // letting a browser guess "application/x-sldprt" on one platform only.
  sldprt: 'application/octet-stream', sldasm: 'application/octet-stream', slddrw: 'application/octet-stream',
}

/* ------------------------------------------------------------------ */
/* Small shared parts                                                  */
/* ------------------------------------------------------------------ */

/**
 * Run `work` over every item, never more than `limit` at a time.
 *
 * A pool of runners pulling from a shared cursor, rather than fixed batches:
 * one slow 400 MB part must not hold up three idle slots.
 * forEachLimit : [T] Number (T Number -> Promise) -> Promise<void>
 */
export async function forEachLimit<T>(
  items: readonly T[], limit: number, work: (item: T, index: number) => Promise<void>,
): Promise<void> {
  let cursor = 0
  const runner = async (): Promise<void> => {
    for (;;) {
      const index = cursor++
      if (index >= items.length) return
      await work(items[index], index)
    }
  }
  await Promise.all(Array.from({ length: Math.min(Math.max(1, limit), items.length) }, runner))
}

/** Retry on network failure, but never on a cancellation or a refusal. */
async function withRetries<T>(attempts: number, signal: AbortSignal, work: () => Promise<T>): Promise<T> {
  for (let attempt = 1; ; attempt++) {
    try {
      return await work()
    } catch (error) {
      if (attempt >= attempts || isAbort(error) || signal.aborted) throw error
      await delay(300 * attempt, signal)
    }
  }
}

const delay = (ms: number, signal: AbortSignal): Promise<void> => new Promise((resolve, reject) => {
  const timer = setTimeout(() => { signal.removeEventListener('abort', onAbort); resolve() }, ms)
  const onAbort = (): void => { clearTimeout(timer); reject(abortError()) }
  signal.addEventListener('abort', onAbort, { once: true })
})

/**
 * Make `child` abort whenever `parent` does; returns the undo.
 * Cancelling a whole batch and cancelling one file are then the same mechanism.
 * linkAbort : AbortSignal|undefined AbortController -> (-> void)
 */
function linkAbort(parent: AbortSignal | undefined, child: AbortController): () => void {
  if (!parent) return () => undefined
  if (parent.aborted) { child.abort(); return () => undefined }
  const onAbort = (): void => child.abort()
  parent.addEventListener('abort', onAbort, { once: true })
  return () => parent.removeEventListener('abort', onAbort)
}

/** True when this error means "somebody cancelled", not "something broke". */
export const isAbort = (error: unknown): boolean =>
  error instanceof Error && (error.name === 'AbortError' || error.name === 'TimeoutError')

function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) throw abortError()
}

/** A sentence a person can read, never "[object Object]". */
const messageOf = (error: unknown): string =>
  error instanceof Error ? error.message : typeof error === 'string' ? error : 'something went wrong'

/** The last segment of a path. leafOf : String -> String */
const leafOf = (path: string): string => path.slice(path.lastIndexOf('/') + 1)

/** Start a browser download from a URL, without leaving the page. */
function clickToDownload(url: string, filename: string): void {
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.rel = 'noopener'
  anchor.style.display = 'none'
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
}

/** The same, for bytes we hold ourselves. */
function clickToDownloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  clickToDownload(url, filename)
  // The download has already started; the URL only needs to outlive the click.
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}
